import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, Pen } from 'lucide-react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';

const DisclaimerModal = ({ isOpen, onAccept, onClose }) => {
  console.log('🎭 DisclaimerModal rendered with isOpen:', isOpen);
  
  // HARDCODED DISCLAIMER CONTENT - DIRECTLY AS STRING CONSTANT
  const disclaimerText = `INFORMED CONSENT SYSTEM - LEGAL DISCLAIMER AND ACKNOWLEDGMENT

By accessing and using the Informed Consent Management System, you acknowledge and agree to the following:

1. ACCURACY OF INFORMATION
   - You are responsible for ensuring all informed consent documents are accurate, current, and legally compliant with applicable federal, state, and local regulations.
   - The system provides templates for convenience but does not constitute legal advice.
   - You must review and customize all documents to meet your specific practice needs and legal requirements.

2. LEGAL COMPLIANCE
   - It is your responsibility to ensure that all informed consent documents comply with applicable laws and regulations in your jurisdiction.
   - You must obtain appropriate legal review of all documents before use with patients.
   - The system does not guarantee legal compliance and should not be relied upon as legal advice.

3. PATIENT RIGHTS AND SAFETY
   - You are responsible for ensuring that patients receive adequate information to make informed decisions.
   - All consents must be obtained in accordance with applicable standards of care and legal requirements.
   - You must maintain appropriate documentation and records as required by law.

4. SYSTEM LIMITATIONS
   - This system is a tool to assist in managing informed consent documentation.
   - It does not replace professional judgment, patient communication, or legal counsel.
   - Technical issues or system errors do not relieve you of your legal obligations.

5. LIABILITY
   - By using this system, you agree that the provider of this software is not liable for any consequences arising from your use of the informed consent documents.
   - You assume all responsibility for the content, accuracy, and legal compliance of documents used with patients.

6. ACKNOWLEDGMENT
   By clicking "I Agree and Accept" below, you acknowledge that:
     * You have read and understood this disclaimer
     * You accept full responsibility for your use of this system
     * You will seek appropriate legal counsel regarding your use of these documents
     * You understand the limitations of this system and will use professional judgment in all patient interactions

This disclaimer was last updated on the date of system implementation. Please review regularly for any updates.`;
  
  console.log('📄 Disclaimer text length:', disclaimerText ? disclaimerText.length : 0);
  console.log('📄 Disclaimer text preview:', disclaimerText ? disclaimerText.substring(0, 100) : 'EMPTY');
  
  const [loading, setLoading] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const signatureRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // NO USEEFFECT NEEDED - Content is hardcoded above

  const handleAccept = async () => {
    if (!agreed) {
      setError('Please check the box to acknowledge you have read and agree to the disclaimer');
      return;
    }

    if (!signerName.trim()) {
      setError('Please enter your full name');
      return;
    }

    if (!signerTitle.trim()) {
      setError('Please enter your title');
      return;
    }

    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setError('Please provide your signature');
      return;
    }

    try {
      setAccepting(true);
      const token = localStorage.getItem('dentalToken');
      
      // Get signature as base64 data URL
      const signatureDataUrl = signatureRef.current.toDataURL();
      
      await axios.post(
        `${backendUrl}/api/informed-consent/disclaimer/accept`,
        {
          ipAddress: null, // Could be populated if needed
          signerName: signerName.trim(),
          signerTitle: signerTitle.trim(),
          signatureData: signatureDataUrl
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      onAccept();
    } catch (error) {
      console.error('Accept disclaimer error:', error);
      setError('Failed to record disclaimer acceptance');
    } finally {
      setAccepting(false);
    }
  };

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] sm:max-h-[90vh] w-[95vw] flex flex-col p-0">
        {/* Fixed Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl font-bold text-red-600">
            Informed Consent Disclaimer
          </DialogTitle>
          <DialogDescription>
            Please read and accept this disclaimer before using the Informed Consent feature
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Disclaimer Text */}
              <div className="h-[300px] sm:h-[350px] border-2 border-gray-300 rounded-md p-4 bg-white overflow-y-auto mb-4">
                <pre className="whitespace-pre-wrap text-sm text-gray-900 font-sans">
                  {disclaimerText}
                </pre>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Signer Information */}
              <div className="space-y-4 py-4 border-t">
                <h3 className="font-semibold text-lg">Signer Information</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="signerName">Full Name *</Label>
                    <Input
                      id="signerName"
                      type="text"
                      placeholder="Enter your full name"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      disabled={accepting}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signerTitle">Title/Position *</Label>
                    <Input
                      id="signerTitle"
                      type="text"
                      placeholder="e.g., Practice Owner, Administrator"
                      value={signerTitle}
                      onChange={(e) => setSignerTitle(e.target.value)}
                      disabled={accepting}
                    />
                  </div>
                </div>

                {/* Signature Pad */}
                <div className="space-y-2">
                  <Label>Digital Signature *</Label>
                  <div className="border-2 border-gray-300 rounded-md bg-white overflow-hidden">
                    <SignatureCanvas
                      ref={signatureRef}
                      canvasProps={{
                        width: 600,
                        height: 150,
                        className: 'signature-canvas w-full'
                      }}
                      backgroundColor="rgb(255, 255, 255)"
                    />
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                    <p className="text-xs text-gray-500">
                      <Pen className="h-3 w-3 inline mr-1" />
                      Sign above using your mouse or touchscreen
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearSignature}
                      disabled={accepting}
                      className="w-full sm:w-auto"
                    >
                      Clear Signature
                    </Button>
                  </div>
                </div>
              </div>

              {/* Agreement Checkbox */}
              <div className="flex items-start space-x-2 py-4 border-t">
                <Checkbox
                  id="disclaimer-agree"
                  checked={agreed}
                  onCheckedChange={setAgreed}
                  disabled={accepting}
                />
                <label
                  htmlFor="disclaimer-agree"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I acknowledge that I have read, understood, and agree to be bound by this
                  Disclaimer. I further acknowledge my responsibility to obtain legal review and to
                  adapt the Materials to my jurisdiction and patient population. I certify that I am
                  authorized to sign this disclaimer on behalf of the practice.
                </label>
              </div>
            </div>

            {/* Fixed Footer */}
            <DialogFooter className="px-6 py-4 border-t bg-gray-50">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={accepting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAccept}
                disabled={!agreed || accepting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {accepting ? 'Processing...' : 'Accept & Continue'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DisclaimerModal;
